import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../lib/api';
import ModalShell, { inputClass, selectClass, textareaClass, labelClass, requiredMark, errorBoxClass } from './ModalShell';

const units = ['pcs', 'kg', 'bag', 'dozen', 'box', 'liter', 'meter', 'pack', 'g', 'ml', 'maund'];

const emptyUnitDraft = () => ({
  unit_name: '',
  conversion_factor: '',
  selling_price: '',
  purchase_price: '',
  is_default: false,
});

const ProductForm = ({ product, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: '',
    unit: 'pcs',
    base_unit: 'pcs',
    current_stock: '0.000000',
    reorder_level: '0.00',
    unit_cost: 0,
    selling_price: 0,
  });
  const [productUnits, setProductUnits] = useState([]);
  const [unitDraft, setUnitDraft] = useState(emptyUnitDraft());
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unitLoading, setUnitLoading] = useState(false);
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [unitEditFactor, setUnitEditFactor] = useState('');
  const [error, setError] = useState('');
  const [productId, setProductId] = useState(product?.id || null);

  useEffect(() => {
    if (product) {
      setFormData({
        sku: product.sku || '',
        name: product.name || '',
        description: product.description || '',
        category: product.category || '',
        unit: product.unit || 'pcs',
        base_unit: product.base_unit || product.unit || 'pcs',
        current_stock: product.current_stock == null ? '0.000000' : String(product.current_stock),
        reorder_level: product.reorder_level == null ? '0.00' : String(product.reorder_level),
        unit_cost: product.unit_cost || 0,
        selling_price: product.selling_price || 0,
      });
      setProductId(product.id);
      fetchUnits(product.id);
    }
  }, [product]);

  const fetchUnits = async (pid) => {
    try { const r = await api.get(`/products/${pid}/units`); setProductUnits(r.data || []); } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name || !formData.unit) { setError('Name and Unit are required'); return; }
    if (parseFloat(formData.unit_cost) < 0 || parseFloat(formData.selling_price) < 0) {
      setError('Numeric fields must be >= 0'); return;
    }
    setLoading(true);
    try {
      const data = {
        name: formData.name,
        description: formData.description || null,
        category: formData.category || null,
        unit: formData.unit,
        base_unit: formData.base_unit || formData.unit,
        reorder_level: formData.reorder_level,
        unit_cost: parseFloat(formData.unit_cost),
        selling_price: parseFloat(formData.selling_price),
      };
      if (productId) {
        await api.put(`/products/${productId}`, data);
        onSuccess();
      } else {
        data.sku = formData.sku || null;
        data.current_stock = formData.current_stock;
        const res = await api.post('/products', data);
        setProductId(res.data.id);
        setError('');
        alert('Product created. You can now add multiple units below, or close the form.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const addUnit = async () => {
    setError('');
    if (!productId) { setError('Save the product first, then add units'); return; }
    if (!unitDraft.unit_name || !unitDraft.conversion_factor) { setError('Unit name and conversion factor are required'); return; }
    setUnitLoading(true);
    try {
      await api.post(`/products/${productId}/units`, {
        unit_name: unitDraft.unit_name,
        conversion_factor: unitDraft.conversion_factor,
        selling_price: parseFloat(unitDraft.selling_price || 0),
        purchase_price: parseFloat(unitDraft.purchase_price || 0),
        is_default: !!unitDraft.is_default,
      });
      setUnitDraft(emptyUnitDraft());
      setShowUnitForm(false);
      await fetchUnits(productId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add unit');
    } finally {
      setUnitLoading(false);
    }
  };

  const startUnitFactorEdit = (unit) => {
    setEditingUnitId(unit.id);
    setUnitEditFactor(String(unit.conversion_factor));
    setError('');
  };

  const saveUnitFactor = async (unitId) => {
    setError('');
    setUnitLoading(true);
    try {
      await api.put(`/products/${productId}/units/${unitId}`, {
        conversion_factor: unitEditFactor,
      });
      setEditingUnitId(null);
      setUnitEditFactor('');
      await fetchUnits(productId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update conversion factor');
    } finally {
      setUnitLoading(false);
    }
  };

  const setDefaultUnit = async (uid) => {
    try { await api.put(`/products/${productId}/units/${uid}`, { is_default: true }); await fetchUnits(productId); } catch (e) { setError(e.response?.data?.detail || 'Failed to set default'); }
  };

  const deleteUnit = async (uid) => {
    if (!confirm('Delete this unit?')) return;
    try { await api.delete(`/products/${productId}/units/${uid}`); await fetchUnits(productId); } catch (e) { setError(e.response?.data?.detail || 'Failed to delete unit'); }
  };

  const unitInputClass = 'w-full bg-white border border-[#d1d5db] rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] outline-none';

  return (
    <ModalShell
      title={productId ? 'Edit Product' : 'Add New Product'}
      onClose={onClose}
      maxWidth="max-w-3xl"
      dataTestId="product-form-modal"
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors" data-testid="cancel-button">{productId ? 'Done' : 'Cancel'}</button>
          <button type="submit" form="product-form" disabled={loading} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50" data-testid="save-product-button">{loading ? 'Saving...' : (productId ? 'Update Product' : 'Add Product')}</button>
        </div>
      }
    >
      <form id="product-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="form-error">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClass}>Product Name {requiredMark}</label>
            <input type="text" className={inputClass} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="product-name-input" />
          </div>
          <div>
            <label className={labelClass}>SKU {!productId && <span className="text-[#6b7280] text-xs">(auto-generated if blank)</span>}</label>
            <input type="text" className={inputClass} value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} disabled={!!productId} data-testid="product-sku-input" />
          </div>
        </div>

        <div className="mb-4">
          <label className={labelClass}>Description</label>
          <textarea className={textareaClass} rows="2" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} data-testid="product-description-input" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={labelClass}>Category</label>
            <input type="text" className={inputClass} value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="e.g., Food, Stationery" data-testid="product-category-input" />
          </div>
          <div>
            <label className={labelClass}>Default Unit {requiredMark}</label>
            <select className={selectClass} value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} required data-testid="product-unit-select">
              {units.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Base Unit {requiredMark} <span className="text-xs text-[#6b7280] font-normal">(smallest)</span></label>
            <select className={selectClass} value={formData.base_unit} onChange={(e) => setFormData({ ...formData, base_unit: e.target.value })} required data-testid="product-base-unit-select">
              {units.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {!productId && (
            <div>
              <label className={labelClass}>Current Stock {requiredMark} <span className="text-xs text-[#6b7280] font-normal">(in base unit)</span></label>
              <input type="number" step="0.000001" min="0" className={inputClass} value={formData.current_stock} onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })} required data-testid="product-stock-input" />
            </div>
          )}
          <div>
            <label className={labelClass}>Reorder Level {requiredMark}</label>
            <input type="number" step="0.01" min="0" className={inputClass} value={formData.reorder_level} onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })} required data-testid="product-reorder-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClass}>Unit Cost (PKR / base unit)</label>
            <input type="number" step="0.01" min="0" className={inputClass} value={formData.unit_cost} onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })} data-testid="product-cost-input" />
          </div>
          <div>
            <label className={labelClass}>Selling Price (PKR / default unit) {requiredMark}</label>
            <input type="number" step="0.01" min="0" className={inputClass} value={formData.selling_price} onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })} required data-testid="product-price-input" />
          </div>
        </div>
      </form>

      {/* Units section */}
      <div className="mt-4 border-t border-[#d1d5db] pt-4">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h3 className="text-sm font-semibold text-[#1a1a2e]">Units of Measure</h3>
            <p className="text-xs text-[#6b7280]">Define extra units (e.g. bag = 50 kg) with their own prices.</p>
          </div>
          {productId && (
            <button type="button" onClick={() => setShowUnitForm(v => !v)} className="text-[#4A7AE0] hover:underline text-sm font-medium flex items-center gap-1" data-testid="toggle-unit-form">
              <Plus className="w-4 h-4" /> Add Unit
            </button>
          )}
        </div>

        {!productId && (
          <div className="text-sm text-[#6b7280] italic p-3 bg-white rounded-lg border border-[#d1d5db]">Save the product first, then add multiple units here.</div>
        )}

        {productId && showUnitForm && (
          <div className="bg-white border border-[#d1d5db] rounded-lg p-4 mb-3" data-testid="unit-form">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1">Unit Name</label>
                <input type="text" className={unitInputClass} placeholder="e.g. bag" value={unitDraft.unit_name} onChange={(e) => setUnitDraft({ ...unitDraft, unit_name: e.target.value })} data-testid="unit-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1">Conversion ({formData.base_unit})</label>
                <input type="number" step="0.0001" min="0" className={unitInputClass} placeholder="e.g. 50" value={unitDraft.conversion_factor} onChange={(e) => setUnitDraft({ ...unitDraft, conversion_factor: e.target.value })} data-testid="unit-conversion-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1">Selling Price</label>
                <input type="number" step="0.01" min="0" className={unitInputClass} value={unitDraft.selling_price} onChange={(e) => setUnitDraft({ ...unitDraft, selling_price: e.target.value })} data-testid="unit-selling-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1">Purchase Price</label>
                <input type="number" step="0.01" min="0" className={unitInputClass} value={unitDraft.purchase_price} onChange={(e) => setUnitDraft({ ...unitDraft, purchase_price: e.target.value })} data-testid="unit-purchase-input" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-xs text-[#1a1a2e] pb-1">
                  <input type="checkbox" checked={unitDraft.is_default} onChange={(e) => setUnitDraft({ ...unitDraft, is_default: e.target.checked })} data-testid="unit-default-checkbox" /> Default
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setShowUnitForm(false); setUnitDraft(emptyUnitDraft()); }} className="px-3 py-1 border border-[#d1d5db] rounded-lg text-sm text-[#1a1a2e] hover:bg-white">Cancel</button>
              <button type="button" onClick={addUnit} disabled={unitLoading} className="px-3 py-1 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg text-sm disabled:opacity-50" data-testid="save-unit-button">{unitLoading ? 'Saving...' : 'Save Unit'}</button>
            </div>
          </div>
        )}

        {productId && (
          <div className="bg-white border border-[#d1d5db] rounded-lg overflow-x-auto">
            <table className="w-full text-sm" data-testid="units-table">
              <thead className="bg-[#f9fafb] text-[#6b7280]">
                <tr>
                  <th className="py-2 px-3 text-left">Unit Name</th>
                  <th className="py-2 px-3 text-left">Conversion ({formData.base_unit})</th>
                  <th className="py-2 px-3 text-right">Selling Price</th>
                  <th className="py-2 px-3 text-right">Purchase Price</th>
                  <th className="py-2 px-3 text-center">Default</th>
                  <th className="py-2 px-3 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {productUnits.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-4 px-3 text-center text-[#9ca3af] text-xs italic">
                      No extra units defined — product is sold by {formData.base_unit || formData.unit} only.
                    </td>
                  </tr>
                ) : (
                  productUnits.map(u => (
                    <tr key={u.id} className="border-t border-[#e5e7eb]" data-testid={`unit-row-${u.unit_name}`}>
                      <td className="py-2 px-3 font-medium text-[#1a1a2e]">{u.unit_name}</td>
                      <td className="py-2 px-3 text-[#6b7280]">
                        {editingUnitId === u.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              step="0.0001"
                              min="0"
                              className={unitInputClass}
                              value={unitEditFactor}
                              onChange={(e) => setUnitEditFactor(e.target.value)}
                              data-testid={`edit-unit-conversion-${u.unit_name}`}
                            />
                            <button type="button" disabled={unitLoading} onClick={() => saveUnitFactor(u.id)} className="text-[#4A7AE0] hover:underline text-xs">Save</button>
                            <button type="button" disabled={unitLoading} onClick={() => { setEditingUnitId(null); setUnitEditFactor(''); }} className="text-[#6b7280] hover:underline text-xs">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span>1 {u.unit_name} = {u.conversion_factor} {formData.base_unit}</span>
                            <button type="button" onClick={() => startUnitFactorEdit(u)} className="text-[#4A7AE0] hover:underline text-xs" data-testid={`edit-unit-${u.unit_name}`}>Edit</button>
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right text-[#1a1a2e]">Rs. {Number(u.selling_price).toLocaleString()}</td>
                      <td className="py-2 px-3 text-right text-[#6b7280]">Rs. {Number(u.purchase_price).toLocaleString()}</td>
                      <td className="py-2 px-3 text-center">
                        {u.is_default ? (
                          <span className="px-2 py-0.5 bg-[#DBEAFE] text-[#1E40AF] rounded-lg text-xs">Default</span>
                        ) : (
                          <button type="button" onClick={() => setDefaultUnit(u.id)} className="text-[#4A7AE0] hover:underline text-xs" data-testid={`set-default-${u.unit_name}`}>Make Default</button>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <button type="button" onClick={() => deleteUnit(u.id)} className="text-[#EF4444] hover:text-[#B91C1C]" data-testid={`delete-unit-${u.unit_name}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default ProductForm;
